import { useEffect, useMemo, useRef, useState } from 'react';
import { Divider, Input as AntInput, Tabs, Table, Button, Modal, Form, Input, Cascader, message, Card, Switch, Select, Tag, Space, Tooltip, AutoComplete } from 'antd';
import type { InputRef } from 'antd';
import type { DefaultOptionType } from 'antd/es/cascader';
import { PlusOutlined, EditOutlined, DeleteOutlined, FileTextOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import { useConfigStore } from '@/store/configStore';
import { useAuthStore } from '@/store/authStore';
import apiClient from '@/api/client';
import type { BspPackageType, Project, FileType, Mountain } from '@/types';
import OperationLogModal from '@/components/OperationLogModal';
import ProjectFormModal from '@/components/ProjectFormModal';

// ── 项目管理搜索：层级联动选择器工具函数（主线/项目 → 山头 → 项目）──
function buildSystemProjectCascaderOptions(projects: Project[]) {
  const mainlineProjects = projects.filter(p => p.is_mainline);
  const normalProjects = projects.filter(p => !p.is_mainline);
  const mountainMap = new Map<string, Project[]>();
  normalProjects.forEach(p => {
    const key = p.mountain?.name ?? '未分配';
    if (!mountainMap.has(key)) mountainMap.set(key, []);
    mountainMap.get(key)!.push(p);
  });
  return [
    { label: '主线', value: '__mainline__', children: mainlineProjects.map(p => ({ label: p.name, value: String(p.id) })) },
    {
      label: '项目', value: '__projects__',
      children: [...mountainMap.entries()].map(([mountain, projs]) => ({
        label: mountain, value: `__mtn__${mountain}`,
        children: projs.map(p => ({ label: p.name, value: String(p.id) })),
      })),
    },
  ];
}

function projectIdToCascaderValue(id: number, projects: Project[]): string[] | undefined {
  const p = projects.find(proj => proj.id === id);
  if (!p) return undefined;
  if (p.is_mainline) return ['__mainline__', String(id)];
  return ['__projects__', `__mtn__${p.mountain?.name ?? '未分配'}`, String(id)];
}

const systemProjectCascaderFilter = (input: string, path: DefaultOptionType[]) =>
  path.some(opt => String(opt.label).toLowerCase().includes(input.toLowerCase()));

// 山头管理表格：取某山头下的有效项目（-999 为"未分组"虚拟行）
const projectsOfMountain = (record: Mountain, projects: Project[]): Project[] =>
  record.id === -999
    ? projects.filter((p) => !p.mountain)
    : projects.filter((p) => p.mountain?.name === record.name);

const SystemConfig = () => {
  const { projects, projectsAll, fileTypes, mountains, bvpMountainNames, rpaProjectOptions, fetchProjects, fetchProjectsAll, fetchFileTypes, fetchMountains, fetchBvpMountainNames, fetchRpaProjectOptions } = useConfigStore();
  const { user } = useAuthStore();
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isFileTypeModalOpen, setIsFileTypeModalOpen] = useState(false);
  const [isMountainModalOpen, setIsMountainModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [editingFileType, setEditingFileType] = useState<FileType | null>(null);
  const [editingMountain, setEditingMountain] = useState<Mountain | null>(null);
  // 独立于 editingMountain：即使是仅存在于 BVP 的虚拟山头（BSP 无对应记录），点击"编辑"也应显示"编辑山头"标题
  const [mountainModalMode, setMountainModalMode] = useState<'create' | 'edit'>('create');
  const [fileTypeForm] = Form.useForm();
  const [mountainForm] = Form.useForm();
  const [isProjectLogModalOpen, setIsProjectLogModalOpen] = useState(false);
  const [isFileTypeLogModalOpen, setIsFileTypeLogModalOpen] = useState(false);
  const [logProject, setLogProject] = useState<Project | null>(null);
  const [logFileType, setLogFileType] = useState<FileType | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<number | undefined>(undefined);
  const [fileTypeSearch, setFileTypeSearch] = useState('');
  const [mountainSearch, setMountainSearch] = useState('');

  // 底软包类型
  const [bspPackageTypes, setBspPackageTypes] = useState<BspPackageType[]>([]);
  const [newPkgTypeName, setNewPkgTypeName] = useState('');
  const newPkgTypeInputRef = useRef<InputRef>(null);

  const fetchBspPackageTypes = async () => {
    try {
      const res = await apiClient.get('/bsp-package-types/');
      setBspPackageTypes(res.data.results ?? res.data);
    } catch {
      // non-critical
    }
  };

  const handleAddBspPackageType = async () => {
    const name = newPkgTypeName.trim();
    if (!name) return;
    try {
      const res = await apiClient.post('/bsp-package-types/', { name });
      const created: BspPackageType = res.data;
      setBspPackageTypes((prev) => [...prev, created]);
      // 自动选中新建的值
      const current: number[] = fileTypeForm.getFieldValue('bsp_package_type_ids') ?? [];
      fileTypeForm.setFieldValue('bsp_package_type_ids', [...current, created.id]);
      setNewPkgTypeName('');
    } catch {
      message.error('创建底软包类型失败');
    }
  };

  // 「飞书项目空间Key」自动补全候选项：来自 RPA /project-map，label 附带简名与已占用山头，
  // 便于按简名/山头名筛选；用户仍可自由输入任意 key（AutoComplete 不限制取值）。
  const rpaKeyOptions = useMemo(
    () =>
      rpaProjectOptions.map((o) => ({
        value: o.project_key,
        label: o.bsp_mountains.length
          ? `${o.short_name || '(未命名)'} · ${o.project_key}（已用：${o.bsp_mountains.join('、')}）`
          : `${o.short_name || '(未命名)'} · ${o.project_key}`,
        searchText: [o.short_name, o.project_key, ...o.bsp_mountains]
          .filter(Boolean)
          .join(' ')
          .toLowerCase(),
      })),
    [rpaProjectOptions],
  );

  useEffect(() => {
    fetchProjects(true);
    fetchProjectsAll();
    fetchFileTypes();
    fetchMountains();
    fetchBvpMountainNames();
    fetchRpaProjectOptions();
    fetchBspPackageTypes();
  }, [fetchProjects, fetchProjectsAll, fetchFileTypes, fetchMountains, fetchBvpMountainNames, fetchRpaProjectOptions]);

  const refreshAll = () => {
    fetchProjects(true);
    fetchProjectsAll();
    fetchMountains();
    fetchBvpMountainNames();
    fetchRpaProjectOptions();
  };

  const handleProjectDelete = (project: Project) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除项目"${project.name}"吗？`,
      okText: '确认',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          await apiClient.delete(`/projects/${project.id}/`);
          message.success('删除成功');
          window.dispatchEvent(new CustomEvent('mainline-project-changed'));
          refreshAll();
        } catch (error) {
          const err = error as { response?: { data?: { detail?: string } } };
          message.error(err.response?.data?.detail || '删除失败');
        }
      },
    });
  };

  // FileType CRUD
  const handleFileTypeSubmit = async (values: Partial<FileType> & { bsp_package_type_ids?: number[] }) => {
    try {
      if (editingFileType) {
        await apiClient.put(`/file-types/${editingFileType.id}/`, values);
        message.success('更新成功');
      } else {
        await apiClient.post('/file-types/', values);
        message.success('创建成功');
      }
      setIsFileTypeModalOpen(false);
      setEditingFileType(null);
      fileTypeForm.resetFields();
      fetchFileTypes();
    } catch {
      message.error('操作失败');
    }
  };

  // Mountain CRUD
  const handleMountainSubmit = async (values: { name: string; project_key?: string; project_ids: number[] }) => {
    try {
      if (editingMountain) {
        await apiClient.put(`/mountains/${editingMountain.id}/`, values);
        message.success('更新成功');
      } else {
        await apiClient.post('/mountains/', values);
        message.success('创建成功');
      }
      setIsMountainModalOpen(false);
      setEditingMountain(null);
      mountainForm.resetFields();
      refreshAll();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { name?: string[] } } };
      const errorMessage = err.response?.data?.name?.[0] || '操作失败';
      message.error(errorMessage);
    }
  };

  const handleMountainDelete = (mountain: Mountain) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除山头"${mountain.name}"吗？删除后，所属项目将不再归属任何山头。`,
      okText: '确认',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          await apiClient.delete(`/mountains/${mountain.id}/`);
          message.success('删除成功');
          refreshAll();
        } catch {
          message.error('删除失败');
        }
      },
    });
  };

  // 删除仅存在于 BVP、BSP 无对应 Mountain 记录的虚拟山头
  const handleBvpOnlyMountainDelete = (mountainName: string) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除山头"${mountainName}"吗？删除后，所属项目将不再归属任何山头。`,
      okText: '确认',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          await apiClient.post('/mountains/delete-bvp-only/', { name: mountainName });
          message.success('删除成功');
          refreshAll();
        } catch {
          message.error('删除失败');
        }
      },
    });
  };

  const projectCascaderOptions = useMemo(() => buildSystemProjectCascaderOptions(projectsAll), [projectsAll]);
  const projectCascaderValue = useMemo(
    () => (selectedProjectId != null ? projectIdToCascaderValue(selectedProjectId, projectsAll) : undefined),
    [selectedProjectId, projectsAll],
  );

  // 车型/子车型由 vehicle_types 派生：一行记录对应一个「车型+子车型」组合，
  // 同一车型会被多个子车型重复带出，需去重。render/筛选/排序必须用同一份派生结果，
  // 否则排序顺序会和显示文本对不上。
  const vehicleTypeLabels = (p: Project): string[] =>
    [...new Set((p.vehicle_types || []).map((v) => v.vehicle_type || String(v.vehicle_type_id)))];
  const vehicleSubtypeLabels = (p: Project): string[] =>
    [...new Set((p.vehicle_types || []).map((v) => v.vehicle_subtype).filter(Boolean))];
  const joinLabels = (labels: string[]) => labels.join('、');

  const projectColumns: ColumnsType<Project> = [
    {
      title: '项目名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      className: 'project-filter-col',
      filters: [...new Set(projectsAll.map((p) => p.name).filter(Boolean))].map((name) => ({ text: name, value: name })),
      filterSearch: true,
      onFilter: (value, record) => record.name === value,
      sorter: (a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
    },
    {
      title: '所属山头',
      key: 'mountain',
      width: 120,
      className: 'project-filter-col',
      filters: [...new Set(projectsAll.map((p) => p.mountain?.name).filter(Boolean))].map((name) => ({ text: name, value: name! })),
      filterSearch: true,
      onFilter: (value, record) => record.mountain?.name === value,
      sorter: (a, b) => (a.mountain?.name ?? '').localeCompare(b.mountain?.name ?? ''),
      render: (_, record) =>
        record.mountain ? (
          <Tag color="blue">{record.mountain.name}</Tag>
        ) : (
          <span style={{ color: '#bbb' }}>-</span>
        ),
    },
    {
      title: '车型',
      key: 'vehicle_types',
      width: 160,
      className: 'project-filter-col',
      filters: [...new Set(projectsAll.flatMap(vehicleTypeLabels))].map((v) => ({ text: v, value: v })),
      filterSearch: true,
      onFilter: (value, record) => vehicleTypeLabels(record).includes(value as string),
      sorter: (a, b) => joinLabels(vehicleTypeLabels(a)).localeCompare(joinLabels(vehicleTypeLabels(b))),
      render: (_, record) => {
        const vehicles = vehicleTypeLabels(record);
        return vehicles.length
          ? vehicles.join('、')
          : <span style={{ color: '#bbb' }}>-</span>;
      },
    },
    {
      title: '子车型',
      key: 'vehicle_subtypes',
      width: 160,
      className: 'project-filter-col',
      filters: [...new Set(projectsAll.flatMap(vehicleSubtypeLabels))].map((v) => ({ text: v, value: v })),
      filterSearch: true,
      onFilter: (value, record) => vehicleSubtypeLabels(record).includes(value as string),
      sorter: (a, b) => joinLabels(vehicleSubtypeLabels(a)).localeCompare(joinLabels(vehicleSubtypeLabels(b))),
      render: (_, record) => {
        const subtypes = vehicleSubtypeLabels(record);
        return subtypes.length
          ? subtypes.join('、')
          : <span style={{ color: '#bbb' }}>-</span>;
      },
    },
    {
      title: '硬件平台',
      dataIndex: 'platform',
      key: 'platform',
      width: 120,
      className: 'project-filter-col',
      filters: [...new Set(projectsAll.map((p) => p.platform).filter(Boolean))].map((v) => ({ text: v!, value: v! })),
      filterSearch: true,
      onFilter: (value, record) => record.platform === value,
      sorter: (a, b) => (a.platform ?? '').localeCompare(b.platform ?? ''),
      render: (value: string) => value || <span style={{ color: '#bbb' }}>-</span>,
    },
    { title: '项目代码', dataIndex: 'code', key: 'code', width: 120 },
    {
      title: '上传第三方',
      key: 'third_party_plugin',
      width: 130,
      className: 'project-filter-col',
      filters: [...new Set(projectsAll.map((p) => p.third_party_plugin?.name).filter(Boolean))]
        .map((v) => ({ text: v!, value: v! })),
      filterSearch: true,
      onFilter: (value, record) => record.third_party_plugin?.name === value,
      sorter: (a, b) => (a.third_party_plugin?.name ?? '').localeCompare(b.third_party_plugin?.name ?? ''),
      render: (_, record) =>
        record.third_party_plugin
          ? <Tag color="geekblue">{record.third_party_plugin.name}</Tag>
          : <span style={{ color: '#bbb' }}>-</span>,
    },
    {
      title: '底软供应商',
      key: 'software_suppliers',
      width: 140,
      className: 'project-filter-col',
      filters: [...new Set(projectsAll.flatMap((p) => p.software_suppliers ?? []).map((s) => s.name))]
        .map((v) => ({ text: v, value: v })),
      filterSearch: true,
      onFilter: (value, record) => !!record.software_suppliers?.some((s) => s.name === value),
      sorter: (a, b) =>
        (a.software_suppliers ?? []).map((s) => s.name).join('、')
          .localeCompare((b.software_suppliers ?? []).map((s) => s.name).join('、')),
      render: (_, record) =>
        record.software_suppliers?.length
          ? (
            <Space size={[4, 4]} wrap>
              {record.software_suppliers.map((s) => <Tag key={s.id} color="purple">{s.name}</Tag>)}
            </Space>
          )
          : <span style={{ color: '#bbb' }}>-</span>,
    },
    {
      title: '负责人',
      key: 'owner',
      width: 120,
      filters: [...new Set(projectsAll.map((p) => p.owner).filter(Boolean))].map((name) => ({ text: name, value: name! })),
      filterSearch: true,
      onFilter: (value, record) => record.owner === value,
      render: (_, record) => record.owner || '-',
    },
    {
      title: '具有更改需求的用户',
      key: 'requirement_change_users',
      width: 160,
      render: (_, record) =>
        record.requirement_change_users && record.requirement_change_users.length > 0
          ? record.requirement_change_users.map((u) => u.username).join('、')
          : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      render: (_, record) => {
        const userCanEdit =
          user?.user_type === 'internal' && (
            user.username === (record.owner ?? '') ||
            (record.requirement_change_users ?? []).some((u) => u.id === user.id)
          );
        return (
        <Space size={4}>
          <Tooltip title="编辑">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingProject(record);
                setIsProjectModalOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title={record.is_mainline ? '主线项目不可删除' : !userCanEdit ? '无需求编辑权限' : '删除'}>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={!userCanEdit || !!record.is_mainline}
              onClick={() => userCanEdit && !record.is_mainline && handleProjectDelete(record)}
            />
          </Tooltip>
          <Tooltip title="日志">
            <Button
              type="text"
              icon={<FileTextOutlined />}
              onClick={() => {
                setLogProject(record);
                setIsProjectLogModalOpen(true);
              }}
            />
          </Tooltip>
        </Space>
        );
      },
    },
  ];

  const FILE_TYPE_DELETE_USERS = ['wenqiang.zhao', 'burt.yan'];
  const canDeleteFileType = FILE_TYPE_DELETE_USERS.includes(user?.username ?? '');

  const handleFileTypeDelete = (record: FileType) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除文件类型"${record.name}"吗？`,
      okText: '确认',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          await apiClient.delete(`/file-types/${record.id}/`);
          message.success('删除成功');
          fetchFileTypes();
        } catch {
          message.error('删除失败');
        }
      },
    });
  };

  const fileTypeColumns: ColumnsType<FileType> = [
    {
      title: '类型名称',
      dataIndex: 'name',
      key: 'name',
      width: '15%',
      sorter: (a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
      filters: [...new Set(fileTypes.map((ft) => ft.name))].map((n) => ({ text: n, value: n })),
      onFilter: (value, record) => record.name === value,
    },
    { title: '类型代码', dataIndex: 'code', key: 'code', width: '12%' },
    { title: '类型说明', dataIndex: 'description', key: 'description', width: '25%', render: (v) => v || '-' },
    {
      title: '底软包类型',
      key: 'bsp_package_types',
      width: '25%',
      filters: bspPackageTypes.map((t) => ({ text: t.name, value: t.id })),
      onFilter: (value, record) => (record.bsp_package_types ?? []).some((t) => t.id === value),
      render: (_, record) =>
        record.bsp_package_types && record.bsp_package_types.length > 0 ? (
          <Space wrap size={4}>
            {record.bsp_package_types.map((t) => (
              <Tag key={t.id} color="blue">{t.name}</Tag>
            ))}
          </Space>
        ) : (
          <span style={{ color: '#bbb' }}>-</span>
        ),
    },
    {
      title: '多文件',
      dataIndex: 'allow_multiple_files',
      key: 'allow_multiple_files',
      width: 90,
      sorter: (a, b) => Number(a.allow_multiple_files ?? false) - Number(b.allow_multiple_files ?? false),
      filters: [
        { text: '是', value: true },
        { text: '否', value: false },
      ],
      onFilter: (value, record) => (record.allow_multiple_files ?? false) === value,
      render: (allow) => (allow ? '是' : '否'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingFileType(record);
              fileTypeForm.setFieldsValue({
                ...record,
                bsp_package_type_ids: (record.bsp_package_types ?? []).map((t) => t.id),
              });
              setIsFileTypeModalOpen(true);
            }}
          >
            编辑
          </Button>
          <Button
            type="link"
            icon={<FileTextOutlined />}
            onClick={() => {
              setLogFileType(record);
              setIsFileTypeLogModalOpen(true);
            }}
          >
            日志
          </Button>
          {canDeleteFileType && (
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleFileTypeDelete(record)}
            >
              删除
            </Button>
          )}
        </>
      ),
    },
  ];

  // 从 projects store（BVP 有效山头）构建山头管理表格数据，与 BVP 保持一致
  // BSP mountains store 仅用于获取 sort_order/is_active 等元数据，不作为主数据源
  const mountainTableData: (Mountain & { _bspId?: number })[] = (() => {
    const seen = new Map<string, Mountain & { _bspId?: number }>();
    for (const p of projects) {
      const name = p.mountain?.name;
      if (name && !seen.has(name)) {
        // 按名称（大小写不敏感）找对应的 BSP 山头，用于获取元数据和编辑 ID
        const bspMountain = mountains.find((m) => m.name.toLowerCase() === name.toLowerCase());
        seen.set(name, {
          id: bspMountain?.id ?? -(seen.size + 1),
          name, // 以 BVP 名称为准
          sort_order: bspMountain?.sort_order ?? 999,
          is_active: bspMountain?.is_active ?? true,
          project_key: bspMountain?.project_key ?? '',
          short_name: bspMountain?.short_name ?? '',
          projects: bspMountain?.projects ?? [],
          created_at: bspMountain?.created_at ?? '',
          updated_at: bspMountain?.updated_at ?? '',
          _bspId: bspMountain?.id,
        });
      }
    }
    // 补充 BVP 中存在但没有任何项目关联的山头（如测试用的空山头），保证 BSP 也能看到
    for (const name of bvpMountainNames) {
      if (name && !seen.has(name)) {
        const bspMountain = mountains.find((m) => m.name.toLowerCase() === name.toLowerCase());
        seen.set(name, {
          id: bspMountain?.id ?? -(seen.size + 1),
          name,
          sort_order: bspMountain?.sort_order ?? 999,
          is_active: bspMountain?.is_active ?? true,
          project_key: bspMountain?.project_key ?? '',
          short_name: bspMountain?.short_name ?? '',
          projects: bspMountain?.projects ?? [],
          created_at: bspMountain?.created_at ?? '',
          updated_at: bspMountain?.updated_at ?? '',
          _bspId: bspMountain?.id,
        });
      }
    }
    const items = Array.from(seen.values()).sort(
      (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)
    );
    // 追加"未分组"虚拟行
    if (projects.some((p) => !p.mountain)) {
      items.push({
        id: -999,
        name: '未分组',
        sort_order: 9999,
        is_active: false,
        project_key: '',
        short_name: '',
        projects: [],
        created_at: '',
        updated_at: '',
      });
    }
    return items.filter((m) => m.name.toLowerCase().includes(mountainSearch.toLowerCase()));
  })();

  const mountainColumns: ColumnsType<Mountain & { _bspId?: number }> = [
    { title: '山头名称', dataIndex: 'name', key: 'name', width: 160 },
    {
      // 每个项目一行「项目名：车型/子车型/硬件平台」，罗列该项目下所有组合，
      // 组合间以英文逗号分隔；子车型或硬件平台为空时跳过该段；项目无车型时以灰色「-」占位
      title: '包含项目 / 车型 / 子车型 / 硬件平台',
      key: 'projects_vehicle_types',
      render: (_, record) => {
        // 使用 projects store 的有效山头（BVP 来源）来展示项目归属
        const effectiveProjects = projectsOfMountain(record, projects);
        if (effectiveProjects.length === 0) {
          return <span style={{ color: '#bbb' }}>暂无项目</span>;
        }
        return (
          <Space direction="vertical" size={2}>
            {effectiveProjects.map((p) => {
              const combos = [
                ...new Set(
                  (p.vehicle_types ?? []).map((v) =>
                    [v.vehicle_type || v.vehicle_type_id, v.vehicle_subtype, p.platform]
                      .filter(Boolean)
                      .join('/'),
                  ),
                ),
              ].join(',   ');
              return (
                <div key={p.id}>
                  <span style={{ color: '#999' }}>{p.name}：</span>
                  {combos || <span style={{ color: '#bbb' }}>-</span>}
                </div>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      render: (_, record) => {
        // "未分组" 是纯虚拟行，不可编辑/删除
        if (record.id === -999) return null;

        const bspMountain = record._bspId ? mountains.find((m) => m.id === record._bspId) : undefined;

        if (!bspMountain) {
          // 仅存在于 BVP 的虚拟山头（BSP 无对应记录）：可编辑（保存时会在 BSP 创建同名山头）、可删除
          return (
            <Space size={4} wrap={false}>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => {
                  setEditingMountain(null);
                  setMountainModalMode('edit');
                  mountainForm.setFieldsValue({
                    name: record.name,
                    project_key: record.project_key || '',
                    project_ids: projects
                      .filter((p) => p.mountain?.name === record.name)
                      .map((p) => p.id),
                  });
                  setIsMountainModalOpen(true);
                }}
              >
                编辑
              </Button>
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleBvpOnlyMountainDelete(record.name)}
              >
                删除
              </Button>
            </Space>
          );
        }

        return (
          <Space size={4} wrap={false}>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingMountain(bspMountain);
                setMountainModalMode('edit');
                mountainForm.setFieldsValue({
                  name: bspMountain.name,
                  project_key: bspMountain.project_key || '',
                  project_ids: projects
                    .filter((p) => p.mountain?.name === record.name)
                    .map((p) => p.id),
                });
                setIsMountainModalOpen(true);
              }}
            >
              编辑
            </Button>
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleMountainDelete(bspMountain)}
            >
              删除
            </Button>
          </Space>
        );
      },
    },
  ];

  const items = [
    {
      key: 'projects',
      label: '项目管理',
      children: (
        <>
          <div className="mb-4 flex gap-2">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingProject(null);
                setIsProjectModalOpen(true);
              }}
            >
              新增项目
            </Button>
            <Cascader
              options={projectCascaderOptions}
              style={{ width: 240 }}
              placeholder="搜索/选择项目"
              value={projectCascaderValue}
              onChange={(v) => setSelectedProjectId(v?.length ? parseInt(v[v.length - 1], 10) : undefined)}
              showSearch={{ filter: systemProjectCascaderFilter }}
              allowClear
              expandTrigger="click"
              displayRender={(labels) => labels[labels.length - 1]}
            />
          </div>
          <Table
            className="project-table"
            columns={projectColumns}
            dataSource={selectedProjectId ? projectsAll.filter((p) => p.id === selectedProjectId) : projectsAll}
            rowKey="id"
          />
        </>
      ),
    },
    {
      key: 'fileTypes',
      label: '文件类型',
      children: (
        <>
          <div className="mb-4 flex gap-2">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingFileType(null);
                fileTypeForm.resetFields();
                setIsFileTypeModalOpen(true);
              }}
            >
              新增类型
            </Button>
            <Input.Search
              placeholder="搜索文件类型名称或代码"
              value={fileTypeSearch}
              onChange={(e) => setFileTypeSearch(e.target.value)}
              allowClear
              style={{ width: 240 }}
            />
          </div>
          <Table
            columns={fileTypeColumns}
            dataSource={fileTypes.filter(
              (ft) =>
                ft.name.toLowerCase().includes(fileTypeSearch.toLowerCase()) ||
                ft.code.toLowerCase().includes(fileTypeSearch.toLowerCase())
            )}
            rowKey="id"
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total) => `共 ${total} 条`,
            }}
          />
        </>
      ),
    },
    {
      key: 'mountains',
      label: '山头管理',
      children: (
        <>
          <div className="mb-4 flex gap-2">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingMountain(null);
                setMountainModalMode('create');
                mountainForm.resetFields();
                setIsMountainModalOpen(true);
              }}
            >
              新建山头
            </Button>
            <Input.Search
              placeholder="搜索山头名称"
              value={mountainSearch}
              onChange={(e) => setMountainSearch(e.target.value)}
              allowClear
              style={{ width: 240 }}
            />
          </div>
          <Table
            columns={mountainColumns}
            dataSource={mountainTableData}
            rowKey="name"
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
          />
        </>
      ),
    },
  ];

  return (
    <Card title="系统配置">
      <Tabs items={items} />

      {/* Project Modal */}
      <ProjectFormModal
        open={isProjectModalOpen}
        project={editingProject}
        defaultMainline={false}
        onSuccess={() => {
          setIsProjectModalOpen(false);
          setEditingProject(null);
          refreshAll();
        }}
        onCancel={() => {
          setIsProjectModalOpen(false);
          setEditingProject(null);
        }}
      />

      {/* FileType Modal */}
      <Modal
        title={editingFileType ? '编辑文件类型' : '新增文件类型'}
        open={isFileTypeModalOpen}
        onCancel={() => setIsFileTypeModalOpen(false)}
        onOk={() => fileTypeForm.submit()}
      >
        <Form form={fileTypeForm} layout="vertical" onFinish={handleFileTypeSubmit}>
          <Form.Item name="name" label="类型名称" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="code" label="类型代码" rules={[{ required: true }]}>
            <Input disabled={!!editingFileType} />
          </Form.Item>
          <Form.Item name="description" label="类型说明">
            <Input placeholder="对该文件类型的简要说明（可选）" maxLength={200} />
          </Form.Item>
          <Form.Item
            name="allow_multiple_files"
            label="支持多文件上传"
            valuePropName="checked"
            initialValue={false}
            tooltip="是否允许该文件类型上传多个附件（支持上传、选已有和 Artifact 模式，Git模式不支持）"
          >
            <Switch />
          </Form.Item>
          <Form.Item name="bsp_package_type_ids" label="底软包类型" initialValue={[]}>
            <Select
              mode="multiple"
              placeholder="选择底软包类型（可多选）"
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={bspPackageTypes.map((t) => ({ value: t.id, label: t.name }))}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <Space style={{ padding: '0 8px 4px' }}>
                    <AntInput
                      ref={newPkgTypeInputRef}
                      placeholder="新建底软包类型"
                      value={newPkgTypeName}
                      onChange={(e) => setNewPkgTypeName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddBspPackageType();
                        }
                      }}
                      style={{ width: 180 }}
                    />
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      onClick={handleAddBspPackageType}
                    >
                      新建
                    </Button>
                  </Space>
                </>
              )}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Mountain Modal */}
      <Modal
        title={mountainModalMode === 'edit' ? '编辑山头' : '新建山头'}
        open={isMountainModalOpen}
        onCancel={() => setIsMountainModalOpen(false)}
        onOk={() => mountainForm.submit()}
        width={520}
      >
        <Form form={mountainForm} layout="vertical" onFinish={handleMountainSubmit}>
          <Form.Item name="name" label="山头名称" rules={[{ required: true, message: '请输入山头名称' }]}>
            <Input placeholder="请输入山头名称" />
          </Form.Item>
          <Form.Item
            name="project_key"
            label="飞书项目空间Key"
            tooltip="该山头对应的飞书项目空间 project_key（可从 RPA 项目空间列表中选择，也可手动填写）"
          >
            <AutoComplete
              options={rpaKeyOptions}
              placeholder="从下拉选择，或手动填写 project_key"
              allowClear
              filterOption={(input, option) =>
                String((option as { searchText?: string } | undefined)?.searchText ?? '')
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item name="project_ids" label="包含项目" initialValue={[]}>
            <Select
              mode="multiple"
              placeholder="选择属于该山头的项目（可多选）"
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={projects.map((p) => ({ value: p.id, label: p.name }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Project Operation Log Modal */}
      {logProject && (
        <OperationLogModal
          open={isProjectLogModalOpen}
          onCancel={() => {
            setIsProjectLogModalOpen(false);
            setLogProject(null);
          }}
          targetType="project"
          targetId={logProject.id}
          title={`${logProject.name} - 操作日志`}
        />
      )}

      {/* FileType Operation Log Modal */}
      {logFileType && (
        <OperationLogModal
          open={isFileTypeLogModalOpen}
          onCancel={() => {
            setIsFileTypeLogModalOpen(false);
            setLogFileType(null);
          }}
          targetType="file_type"
          targetId={logFileType.id}
          title={`${logFileType.name} - 操作日志`}
        />
      )}
    </Card>
  );
};

export default SystemConfig;
